import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  IPreassignation,
  IPreassignationForm,
  IPreassignationsParam,
} from "./types"
import api from "services/api"

export function usePreassignations(
  params?: IPreassignationsParam,
  config?: UseQueryOptions<IPreassignation[]>
) {
  return useQuery<IPreassignation[]>(
    ["suite", "time", "assignations", "preassignations", params],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useCreatePreassignation(
  config?: UseMutationOptions<unknown, unknown, IPreassignationForm>
) {
  const queryClient = useQueryClient()
  return useMutation(
    (data) => api.post(`suite/time/assignations/preassignations`, data),
    {
      ...config,
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.invalidateQueries([
          "suite",
          "time",
          "assignations",
          "preassignations",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "external",
        ])
      },
    }
  )
}

export function useDeletePreassignation(
  config?: UseMutationOptions<{ data: { id: string } }, unknown, string>
) {
  const queryClient = useQueryClient()
  return useMutation<{ data: { id: string } }, unknown, string>(
    (preassignationId) =>
      api.delete(`suite/time/assignations/preassignations/${preassignationId}`),
    {
      ...config,
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.setQueryData<IPreassignation[]>(
          ["suite", "time", "assignations", "preassignations", null],
          (queryData) => queryData?.filter((el) => el.id !== data.data.id)
        )
        queryClient.invalidateQueries([
          "suite",
          "time",
          "assignations",
          "preassignations",
        ])
        queryClient.invalidateQueries([
          "operation",
          "production",
          "events",
          "external",
        ])
      },
    }
  )
}

export interface ISession {
  id: string
  name: string
  description?: string
  start_date?: string
  end_date?: string
  activities_count: number
  status: "draft" | "published" | "progress" | "closed" | "terminated"
  displayed_status: string
  subscription_start_date: string
  subscription_start_time: string
}

export function useExtSessions(
  params?: any,
  config?: UseQueryOptions<ISession[]>
) {
  return useQuery<ISession[]>(
    ["tactical", "production", "sessions", "external", params],
    {
      ...config,
    }
  )
}

export function useExtCurrentSession(config?: UseQueryOptions<ISession>) {
  return useQuery<ISession>(
    ["tactical", "production", "sessions", "external", "current"],
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      ...config,
    }
  )
}

export interface IExtEventParams {
  check_pre_subscription_for_member_id?: string
  check_subscription_for_member_id?: string
  check_waiting_list_for_member_id?: string
  check_preassignation_for_employee?: string | "user"
}

export interface IExtEvent {
  id: string
  activity_id: string
  name: string
  start_date: string
  start_hour: string
  end_date?: string
  end_hour?: string
  division?: string
  status: string
  session: ISession
  displayed_status: string
  enrollment: string
  displayed_enrollment: string
  place: {
    name: string
  }
  max_participants?: number
  available_seats: number
  requested_employee_is_already_pre_assigned?: boolean
  requested_member_is_already_subscribed?: boolean
  requested_member_is_already_in_waiting_list?: boolean
  requested_member_is_already_pre_subscribed?: boolean
  waiting_members_count?: number
}

export function useExtEvents(
  params: IPaginationParams & IExtEventParams & any,
  config?: UseQueryOptions<IExtEvent[]>
) {
  return useQuery<IExtEvent[]>(
    ["operation", "production", "events", "external", params],
    {
      ...config,
    }
  )
}
